import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { FetchMC, FetchMCs, FetchMCsCounts } from 'src/constants/actionTypes'
import { mcPageSizeOptions, mcInitialSortFields, mcColumns } from 'src/constants/mc/mcGrid'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'
import { IMCsTableContext, MCsTableContext } from './MCsTable.context'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'
import { getSelectedMarket } from 'src/selectors/market'
import { MCsHeader } from '../grid/MCsHeader'
import { handleApiError } from 'src/utils/errorHandlers'
import { Pagination } from 'src/interfaces/table'
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro'
import { useGridColumnPreferences } from 'src/utils/hooks/grid/useGridColumnPreferences'
import { MCsContext } from '../mcs/MCs.context'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { EmptyMCs } from '../grid/EmptyMCs'
import { MCStatus } from 'src/constants/mc/enums'
import { MC, MCDetails } from 'src/interfaces/mc'
import { MCDetailsModal } from 'src/components/mc-details/MCDetailsModal'

interface FetchMCsParams {
  limit?: number
  offset?: number
  searchQuery: string
  tagsFilter: Array<string>
  sortModel: GridSortModel
  selectedStatus: MCStatus
}

interface MCsTableProps {
  selectedStatus: MCStatus
}

const getRowId = (row: MC) => row.masterContractorId

const LOCKED_COLUMNS = ['id']

const MCsTableComponent = ({ selectedStatus }: MCsTableProps) => {
  const dispatch = useDispatch()

  const mcDetailsRequestRef = useRef(0)

  const selectedMarket = useSelector(getSelectedMarket)

  const { searchQuery } = useContext(MCsContext)

  const { paginationModel, limit, offset, setPaginationModel } =
    useGridPagination(mcPageSizeOptions)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [mcs, setMCs] = useState<Array<MC>>([])
  const [totalMCs, setTotalMCs] = useState<number>(0)
  const [tagsFilter, setTagsFilter] = useState<Array<string>>([])
  const [selectedMCId, setSelectedMCId] = useState<string | null>(null)
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [mcDetails, setMCDetails] = useState<MCDetails | null>(null)
  const [sortModel, setSortModel] = useState<GridSortModel>(
    mcInitialSortFields[selectedStatus]
      ? [
          {
            field: mcInitialSortFields[selectedStatus] || 'invitedAt',
            sort: 'desc',
          },
        ]
      : [],
  )

  const fetchMCs = useCallback(
    async ({
      limit,
      offset,
      searchQuery,
      sortModel,
      selectedStatus,
      tagsFilter,
    }: FetchMCsParams) => {
      setIsLoading(true)

      try {
        const params: any = {
          limit,
          offset,
          status: selectedStatus,
        }

        if (searchQuery) {
          params.search = searchQuery
        }

        if (tagsFilter?.length) {
          params.tags = tagsFilter
        }

        if (sortModel && sortModel[0]) {
          params.sort = `${sortModel[0]?.field}:${sortModel[0]?.sort?.toUpperCase()}`
        }

        const payload: { data: Array<MC>; pagination: Pagination } = await createAsyncAction(
          dispatch,
          FetchMCs.request(params),
        )

        setMCs(payload.data)
        setTotalMCs(payload.pagination.total)
      } catch (err: unknown) {
        handleApiError(err)
      }

      setIsLoading(false)
    },
    [selectedMarket?.id],
  )

  const fetchMCsCounts = useCallback(() => {
    const params: any = {}

    if (selectedMarket?.id) {
      params.partnerMarketId = selectedMarket?.id
    }

    dispatch(FetchMCsCounts.request(params))
  }, [selectedMarket?.id])

  const refreshMCs = useCallback(async () => {
    await fetchMCs({
      limit,
      offset,
      searchQuery,
      sortModel,
      tagsFilter,
      selectedStatus,
    })
    fetchMCsCounts()
  }, [limit, offset, searchQuery, sortModel, tagsFilter, selectedStatus, fetchMCs, fetchMCsCounts])

  useEffect(() => {
    fetchMCs({
      limit,
      offset,
      searchQuery,
      sortModel,
      tagsFilter,
      selectedStatus,
    })
  }, [limit, offset, searchQuery, sortModel, tagsFilter, selectedStatus, selectedMarket?.id])

  const handleRowClick = useCallback(({ row }: { row: MC }) => {
    setSelectedMCId(row.masterContractorId)
  }, [])

  const handleSortModelChange = (newModel: GridSortModel) => {
    setSortModel(newModel)
  }

  const handleChangePagination = useCallback((paginationModel: GridPaginationModel) => {
    setPaginationModel(paginationModel)
  }, [])

  const fetchMCDetails = useCallback(async (masterContractorId: string) => {
    const requestId = ++mcDetailsRequestRef.current

    try {
      const mc: MCDetails = await createAsyncAction(
        dispatch,
        FetchMC.request({ masterContractorId }),
      )

      if (mcDetailsRequestRef.current === requestId) {
        setMCDetails(mc)
      }
    } catch (err) {
      if (mcDetailsRequestRef.current === requestId) {
        handleApiError(err)
      }
    }
  }, [])

  useEffect(() => {
    if (!selectedMCId) {
      mcDetailsRequestRef.current += 1
      setMCDetails(null)
    } else {
      fetchMCDetails(selectedMCId)
    }
  }, [selectedMCId])

  const closeMCDetails = useCallback(() => {
    mcDetailsRequestRef.current += 1
    setMCDetails(null)
    setSelectedMCId(null)
  }, [])

  const refreshMCDetails = useCallback(async () => {
    if (selectedMCId) {
      await fetchMCDetails(selectedMCId)
    }

    refreshMCs()
  }, [selectedMCId, refreshMCs])

  const { hasPermission } = useCheckPermissions()

  const columns = useMemo(() => {
    const cols = mcColumns[selectedStatus]

    if (hasPermission(UserPermission.TagsView)) {
      return cols
    }

    return cols.filter((col) => col.field !== 'tags')
  }, [selectedStatus, hasPermission])

  const {
    columnVisibilityModel,
    pinnedColumns,
    orderedColumns,
    onColumnVisibilityModelChange,
    onPinnedColumnsChange,
    onColumnOrderChange,
    onToggleColumnVisibility,
    onPinColumn,
    onUnpinColumn,
  } = useGridColumnPreferences({
    tableKey: `mcs-${selectedStatus}`,
    columns,
    lockedColumns: LOCKED_COLUMNS,
  })

  const mcsContextValue = useMemo<IMCsTableContext>(() => {
    return {
      tagsFilter,
      setTagsFilter,

      refreshMCs,

      columnsConfig: columns,
      columnVisibilityModel,
      pinnedColumns,
      lockedColumns: LOCKED_COLUMNS,
      onToggleColumnVisibility,
      onColumnVisibilityModelChange,
      onPinColumn,
      onUnpinColumn,
    }
  }, [
    tagsFilter,
    setTagsFilter,
    refreshMCs,
    columns,
    columnVisibilityModel,
    pinnedColumns,
    onToggleColumnVisibility,
    onColumnVisibilityModelChange,
    onPinColumn,
    onUnpinColumn,
  ])

  return (
    <MCsTableContext.Provider value={mcsContextValue}>
      <MCsHeader />
      <GridTable<MC>
        rows={mcs}
        isLoading={isLoading}
        columns={orderedColumns}
        paginationModel={paginationModel}
        pageSizeOptions={mcPageSizeOptions}
        rowCount={totalMCs}
        sortModel={sortModel}
        columnVisibilityModel={columnVisibilityModel}
        pinnedColumns={pinnedColumns}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        onPinnedColumnsChange={onPinnedColumnsChange}
        onColumnOrderChange={onColumnOrderChange}
        onSortModelChange={handleSortModelChange}
        onPaginationModelChange={handleChangePagination}
        onRowClick={handleRowClick}
        getRowId={getRowId}
        EmptyElement={EmptyMCs}
        tableDataTestId="master-contractors-list-main-table"
        rowDataTestIdPrefix="master-contractors-list-main"
      />
      {mcDetails && (
        <MCDetailsModal
          mcDetails={mcDetails}
          onClose={closeMCDetails}
          onRefresh={refreshMCDetails}
        />
      )}
    </MCsTableContext.Provider>
  )
}

export const MCsTable = memo(MCsTableComponent)
